import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import CurrencyInput from 'react-currency-input-field';
import numeral from 'numeral';

const PE_VALUATION_QUERY = gql`
  query peValuation($numberOfClients: Float!, $assetsUnderManagement: Float!, $feeBasedRevenue: Float!, $trailerRevenue: Float!, $directRevenue: Float!, $commissionRevenue: Float!) {
    peValuation(
      numberOfClients: $numberOfClients
      assetsUnderManagement: $assetsUnderManagement
      feeBasedRevenue: $feeBasedRevenue
      trailerRevenue: $trailerRevenue
      directRevenue: $directRevenue
      commissionRevenue: $commissionRevenue
    )
  }
`;

const PriceDetails = ({ formData, setFormData, handleCurrencyChange, company, onValidationChange }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const [errors, setErrors] = useState({});
  const [isNegotiable, setIsNegotiable] = useState(false);

  const totalRevenue = (formData.feeBasedRevenue || 0) + 
                      (formData.trailerRevenue || 0) + 
                      (formData.directRevenue || 0) + 
                      (formData.commissionRevenue || 0);

  // Query for PE valuation
  const { data: valuationData, loading: valuationLoading } = useQuery(PE_VALUATION_QUERY, {
    variables: {
      numberOfClients: parseFloat(formData.numberOfClients) || 0,
      assetsUnderManagement: formData.assetsUnderManagement || 0,
      feeBasedRevenue: formData.feeBasedRevenue || 0,
      trailerRevenue: formData.trailerRevenue || 0,
      directRevenue: formData.directRevenue || 0,
      commissionRevenue: formData.commissionRevenue || 0
    },
    skip: totalRevenue === 0
  });

  const validateForm = () => {
    const newErrors = {};
    
    if (!isNegotiable && (!formData.askingPrice || formData.askingPrice <= 0)) {
      newErrors.askingPrice = intl.formatMessage({ id: 'succession.partial_sales.price_details.error_asking_price_required' });
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;
    
    // Notify parent component about validation state
    if (onValidationChange) {
      onValidationChange(hasErrors);
    }
    
    return !hasErrors;
  };

  const handleNegotiableChange = (checked) => {
    setIsNegotiable(checked);
    
    if (checked) {
      // Set price to 0 when negotiable is checked
      setFormData({
        ...formData,
        askingPrice: 0,
        isNegotiable: true
      });
    } else {
      // Clear isNegotiable flag when unchecked
      setFormData({
        ...formData,
        isNegotiable: false
      });
    }
    
    // Clear any existing errors
    if (errors.askingPrice) {
      setErrors({
        ...errors,
        askingPrice: ''
      });
    }
  };

  useEffect(() => {
    validateForm();
  }, [formData.askingPrice, isNegotiable]);

  // Initial validation on mount
  useEffect(() => {
    validateForm();
  }, []);

  const getMultipleOfRevenue = () => {
    if (totalRevenue === 0) return 0;
    return (formData.askingPrice || 0) / totalRevenue;
  };

  const getValuationRange = () => {
    if (!valuationData?.peValuation) return null;
    
    try {
      // Check if it's already an object
      if (typeof valuationData.peValuation === 'object') {
        return {
          low: valuationData.peValuation.low || 0,
          high: valuationData.peValuation.high || 0
        };
      }
      
      // Check if it's a string that needs parsing
      if (typeof valuationData.peValuation === 'string') {
        const trimmed = valuationData.peValuation.trim();
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
          return null;
        }
        
        const valuation = JSON.parse(trimmed);
        return {
          low: valuation.low || 0,
          high: valuation.high || 0
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing valuation data:', error, 'Raw data:', valuationData.peValuation);
      return null;
    }
  };

  const valuationRange = getValuationRange();
  const multipleOfRevenue = getMultipleOfRevenue();

  const inputClasses = "w-full px-3 py-2 border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors bg-succession-bg-primary text-succession-text-primary";
  const labelClasses = "block text-sm font-medium mb-2";
  const errorClasses = "mt-1 text-sm text-succession-error";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.price_details.title" />
        </h2>
      </div>

      {/* Asking Price */}
      <div>
        <label className={`${labelClasses} text-succession-text-primary transition-colors duration-300`} style={{paddingBottom: '0px', paddingTop: '0px'}}>
          <FormattedMessage id="succession.partial_sales.price_details.asking_price_label" />
        </label>
        <CurrencyInput
          value={formData.askingPrice}
          onValueChange={(value) => handleCurrencyChange('askingPrice', parseFloat(value) || 0)}
          className={`${inputClasses} ${errors.askingPrice ? 'border-succession-error' : ''} ${isNegotiable ? 'bg-succession-bg-secondary cursor-not-allowed' : ''}`}
          placeholder="$0.00"
          prefix="$"
          decimalsLimit={2}
          allowNegativeValue={false}
          disabled={isNegotiable}
        />
        
        {/* Price is Negotiable Checkbox */}
        <div className="mt-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isNegotiable}
              onChange={(e) => handleNegotiableChange(e.target.checked)}
              className="h-4 w-4 border-succession-border rounded focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 hover:border-succession-primary transition-colors"
              style={{
                accentColor: 'var(--succession-primary)',
                backgroundColor: isNegotiable ? 'var(--succession-primary)' : 'transparent'
              }}
            />
            <span className="text-sm text-succession-text-primary">
              <FormattedMessage id="succession.partial_sales.price_details.price_negotiable" />
            </span>
          </label>
        </div>
        
        {errors.askingPrice && (
          <p className={errorClasses}>{errors.askingPrice}</p>
        )}
        
        {!isNegotiable && formData.askingPrice > 0 && totalRevenue > 0 && (
          <div className="mt-2 text-sm text-succession-text-secondary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.partial_sales.price_details.multiple_of_revenue' }, { value: multipleOfRevenue.toFixed(1) })}
          </div>
        )}
      </div>

      {/* Pricing Guidance */}
      <div className="bg-succession-warning-bg border border-succession-warning rounded-lg p-4">
        <h3 className="text-lg font-semibold text-succession-warning mb-2">
          <FormattedMessage id="succession.partial_sales.price_details.pricing_guidance_title" />
        </h3>
        <ul className="text-sm text-succession-warning space-y-1">
          <li>• <FormattedMessage id="succession.partial_sales.price_details.guidance_1" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.price_details.guidance_2" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.price_details.guidance_3" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.price_details.guidance_4" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.price_details.guidance_5" /></li>
        </ul>
      </div>

      {/* Price Comparison */}
      {!isNegotiable && formData.askingPrice > 0 && valuationRange && (
        <div className="bg-succession-bg-secondary p-4 rounded-lg border border-succession-border">
          <h3 className="text-lg font-semibold mb-3 text-succession-text-primary transition-colors duration-300">
            <FormattedMessage id="succession.partial_sales.price_details.price_comparison_title" />
          </h3>
          <div className="space-y-2 text-sm">
            {formData.askingPrice < valuationRange.low && (
              <div className="text-succession-success font-medium">
                <FormattedMessage id="succession.partial_sales.price_details.below_market" />
              </div>
            )}
            {formData.askingPrice >= valuationRange.low && formData.askingPrice <= valuationRange.high && (
              <div className="text-succession-primary font-medium">
                <FormattedMessage id="succession.partial_sales.price_details.within_market" />
              </div>
            )}
            {formData.askingPrice > valuationRange.high && (
              <div className="text-orange-600 font-medium">
                <FormattedMessage id="succession.partial_sales.price_details.above_market" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceDetails;